import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdAdd,
  MdExpandMore,
  MdChevronRight,
  MdDescription,
  MdFolder,
  MdCreateNewFolder,
  MdMoreVert,
  MdEdit,
  MdArchive,
  MdUnarchive,
  MdVisibility,
  MdVisibilityOff,
  MdAutoAwesome,
  MdArrowUpward,
  MdArrowDownward,
  MdAttachFile,
  MdDelete,
  MdUploadFile,
} from 'react-icons/md';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { tocApi, documentApi, attachmentsApi } from '../../utils/api/DocumentEditorApi';
import { useClass } from '../../utils/ClassContext';
import { useNotifications } from '../common/NotificationManager';

// Sortable Item Component for drag and drop
// Items do NOT shift while dragging — layout stays frozen.
// The active item becomes invisible (DragOverlay shows its ghost instead).
const SortableItem = ({ id, children, type, activeDragId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
  } = useSortable({ id, data: { type } });

  return (
    <div
      ref={setNodeRef}
      style={{ opacity: activeDragId === id ? 0 : 1 }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};

// Context Menu Component
const ContextMenu = ({ isOpen, position, item, onClose, onEdit, onDelete, onArchive, onAddSection, onAddSubsection, onMoveToSection, onMoveToRoot, onToggleVisibility, onEditGuideline, onAddFile, sections }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={handleBackdropClick}
      />
      <div
        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-48"
        style={{
          left: position.x,
          top: position.y,
        }}
      >
        {item?.type === 'section' && (
          <>
            <button
              onClick={() => { onAddSection(item.section_id); onClose(); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <MdAdd size={16} />
              <span>Add Document to Section</span>
            </button>
            <button
              onClick={() => { onAddSubsection(item.section_id); onClose(); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
            >
              <MdCreateNewFolder size={16} />
              <span>Add Subsection</span>
            </button>
            {item?.parent_section_id && (
              <button
                onClick={() => { onMoveToRoot(item.section_id); onClose(); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
              >
                <MdChevronRight size={16} className="rotate-180" />
                <span>Move to Root Level</span>
              </button>
            )}
          </>
        )}

        <button
          onClick={() => { onAddFile(item); onClose(); }}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
        >
          <MdAttachFile size={16} />
          <span>Add Reference File</span>
        </button>

        <button
          onClick={() => { onEdit(item); onClose(); }}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
        >
          <MdEdit size={16} />
          <span>Rename</span>
        </button>

        <button
          onClick={() => { onToggleVisibility(item); onClose(); }}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
        >
          {item?.visible !== false ? (
            <>
              <MdVisibilityOff size={16} />
              <span>Hide from Students</span>
            </>
          ) : (
            <>
              <MdVisibility size={16} />
              <span>Show to Students</span>
            </>
          )}
        </button>

        {item?.type === 'document' && sections.length > 0 && (
          <div className="border-t border-gray-100">
            <div className="px-4 py-2 text-xs text-gray-500 font-medium">Move to Section</div>
            <button
              onClick={() => { onMoveToSection(item.document_id, null); onClose(); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
            >
              Root Level
            </button>
            {sections.map(section => (
              <button
                key={section.section_id}
                onClick={() => { onMoveToSection(item.document_id, section.section_id); onClose(); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
              >
                {section.name}
              </button>
            ))}
          </div>
        )}

        <div className="border-t border-gray-100">
          <button
            onClick={() => { onArchive(item); onClose(); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center space-x-2"
          >
            <MdArchive size={16} />
            <span>Archive</span>
          </button>
        </div>
      </div>
    </>
  );
};

// Edit Modal Component
const EditModal = ({ isOpen, item, onClose, onSave, title }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (isOpen && item) {
      setName(item.name || item.title || '');
    }
  }, [isOpen, item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSave(item, name.trim());
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-full">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter name..."
              autoFocus
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={!name.trim()}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Archive Modal Component
const ArchiveModal = ({ isOpen, onClose, archivedItems, onUnarchive, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Archived Items</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading archived items...</div>
            </div>
          ) : archivedItems.length === 0 ? (
            <div className="text-center py-8">
              <MdArchive size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No archived items found</p>
              <p className="text-sm text-gray-400 mt-2">
                Items you archive will appear here and can be restored later.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {archivedItems.map((item) => (
                <div
                  key={item.itemType === 'section' ? item.section_id : item.document_id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    {item.itemType === 'section' ? (
                      <MdFolder size={18} className="text-yellow-600" />
                    ) : (
                      <MdDescription size={18} className="text-blue-600" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">
                        {item.itemType === 'section' ? item.name : item.title}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.itemType === 'section' ? 'Section' : 'Document'} •
                        Archived {new Date(item.archived_at).toLocaleDateString()}
                        {item.archived_by && (
                          <> by {item.archived_by.firstname} {item.archived_by.lastname}</>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => onUnarchive(item)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <MdUnarchive size={16} />
                    <span>Restore</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TableOfContents = ({ onDocumentSelect, onEditGuideline, className = '', isVisible = true, refreshTrigger = 0, autoExpandSection = null, isStudent = false }) => {
  const { getClassContext } = useClass();
  const { showError, showSuccess } = useNotifications();
  const [tocData, setTocData] = useState({
    sections: [],
    documents: [],
    root_documents: [],
    section_documents: {},
    section_guidelines: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sections are expanded by default; collapsedSections tracks ones the user explicitly closed
  const [collapsedSections, setCollapsedSections] = useState(new Set());



  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    item: null
  });

  // Edit modal state
  const [editModal, setEditModal] = useState({
    isOpen: false,
    item: null,
    title: ''
  });

  // New section modal
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  // New subsection modal — tracks which parent section to nest under
  const [newSubsectionParentId, setNewSubsectionParentId] = useState(null);
  const [showNewSubsectionModal, setShowNewSubsectionModal] = useState(false);

  // Archive modal state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);

  // Attachment state
  const [attachmentTarget, setAttachmentTarget] = useState(null); // { id, type, name }
  const [attachments, setAttachments] = useState({}); // keyed by parent_id
  const [uploadingFile, setUploadingFile] = useState(false);
  const attachFileInputRef = useRef(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const justDraggedRef = useRef(false); // suppress click-toggle immediately after drag release
  // When dragging a section over another section header, track the nest target
  const [nestTargetId, setNestTargetId] = useState(null);
  const nestTargetIdRef = useRef(null); // ref copy so handleDragEnd reads current value
  const nestHoverTimerRef = useRef(null);
  const nestHoverTargetRef = useRef(null); // which section the timer is currently counting for
  // Drop indicator: { overId, position: 'before' | 'after' }
  const [dropIndicator, setDropIndicator] = useState(null);

  // Show error notifications instead of blocking the UI
  useEffect(() => {
    if (error) {
      showError(error);
      setError(null); // Clear error after showing notification
    }
  }, [error, showError]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Root level droppable zone - must be called before any early returns
  const { setNodeRef: setRootDropRef, isOver: isRootOver } = useDroppable({
    id: 'root-drop-zone',
    data: { type: 'root', section_id: null }
  });

  const loadTocData = useCallback(async ({ showSpinner = false } = {}) => {
    try {
      if (showSpinner) setLoading(true);
      setError(null);

      const classContext = getClassContext();
      const response = await tocApi.getTocData(classContext);

      if (response.success) {
        setTocData(response.toc_data);
        if (!isStudent) loadAllSectionAttachments(response.toc_data.sections || []);
        return response.toc_data;
      } else {
        setError(response.error || 'Failed to load table of contents data');
      }
    } catch (err) {
      console.error('Error loading table of contents data:', err);
      setError('Failed to load table of contents data');
    } finally {
      if (showSpinner) setLoading(false);
    }
    return null;
  }, [getClassContext]);

  const loadArchivedItems = useCallback(async () => {
    try {
      setLoadingArchive(true);

      const [documentsResponse, sectionsResponse] = await Promise.all([
        tocApi.getArchivedDocuments(getClassContext()),
        tocApi.getArchivedSections(getClassContext())
      ]);

      const allItems = [];

      if (documentsResponse.success && documentsResponse.documents) {
        allItems.push(...documentsResponse.documents.map(doc => ({ ...doc, itemType: 'document' })));
      }

      if (sectionsResponse.success && sectionsResponse.sections) {
        allItems.push(...sectionsResponse.sections.map(section => ({ ...section, itemType: 'section' })));
      }

      allItems.sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at));

      setArchivedItems(allItems);

      if (!documentsResponse.success && !sectionsResponse.success) {
        setError('Failed to load archived items');
      }
    } catch (err) {
      console.error('Error loading archived items:', err);
      setError('Failed to load archived items');
    } finally {
      setLoadingArchive(false);
    }
  }, [getClassContext]);

  const lastClassIdRef = useRef(null);

  useEffect(() => {
    const classContext = getClassContext();
    const currentClassId = classContext?.class_id;

    if (currentClassId && currentClassId !== lastClassIdRef.current) {
      lastClassIdRef.current = currentClassId;
      loadTocData({ showSpinner: true });
    } else if (!currentClassId && lastClassIdRef.current) {
      lastClassIdRef.current = null;
    }
  }, [loadTocData, getClassContext]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      loadTocData();
    }
  }, [refreshTrigger, loadTocData]);

  useEffect(() => {
    if (autoExpandSection) {
      // With default-expanded logic, ensure it's NOT in collapsedSections
      setCollapsedSections(prev => {
        const next = new Set(prev);
        next.delete(autoExpandSection);
        return next;
      });
    }
  }, [autoExpandSection]);

  useEffect(() => {
    if (showArchiveModal) {
      loadArchivedItems();
    }
  }, [showArchiveModal, loadArchivedItems]);

  const handleDocumentClick = async (document) => {
    try {
      if (onDocumentSelect) {
        const sectionName = document.section_id
          ? tocData.sections.find(s => s.section_id === document.section_id)?.name || null
          : null;
        onDocumentSelect({ ...document, section_name: sectionName });
      }
    } catch (err) {
      console.error('Error opening document:', err);
    }
  };

  const toggleSection = (sectionId) => {
    if (justDraggedRef.current) return; // drag release fires a click — ignore it
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId); // was collapsed → expand
      } else {
        next.add(sectionId); // was expanded → collapse
      }
      return next;
    });
  };

  const isExpanded = (sectionId) => !collapsedSections.has(sectionId);

  const handleContextMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      item: item
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isOpen: false,
      position: { x: 0, y: 0 },
      item: null
    });
  };

  const handleEdit = (item) => {
    setEditModal({
      isOpen: true,
      item: item,
      title: item.type === 'section' ? 'Rename Section' : 'Rename Document'
    });
  };

  const handleSave = async (item, newName) => {
    try {
      if (item.type === 'section') {
        await tocApi.updateSection(item.section_id, newName, '', getClassContext());
      } else {
        await documentApi.updateDocument(item.document_id, newName, item.content || '', null, getClassContext());
      }

      loadTocData();
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save changes');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name || item.title}"?`)) {
      return;
    }

    try {
      if (item.type === 'section') {
        await tocApi.deleteSection(item.section_id, getClassContext());
      } else {
        console.log('Document delete not yet implemented:', item);
      }

      loadTocData();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Failed to delete item');
    }
  };

  const handleArchive = async (item) => {
    if (!window.confirm(`Are you sure you want to archive "${item.name || item.title}"?`)) {
      return;
    }

    try {
      if (item.type === 'section') {
        await tocApi.archiveSection(item.section_id, getClassContext());
      } else {
        await tocApi.archiveDocument(item.document_id, getClassContext());
      }

      showSuccess(`"${item.name || item.title}" has been archived successfully.`);
      loadTocData();
    } catch (err) {
      console.error('Error archiving:', err);
      showError(`Failed to archive "${item.name || item.title}". Please try again.`);
    }
  };

  const handleUnarchive = async (item) => {
    try {
      if (item.itemType === 'section') {
        await tocApi.unarchiveSection(item.section_id, getClassContext());
      } else {
        await tocApi.unarchiveDocument(item.document_id, getClassContext());
      }

      showSuccess(`"${item.itemType === 'section' ? item.name : item.title}" has been restored successfully.`);
      loadArchivedItems();
      loadTocData();
    } catch (err) {
      console.error('Error unarchiving:', err);
      showError(`Failed to restore "${item.itemType === 'section' ? item.name : item.title}". Please try again.`);
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      let response;
      if (item.itemType === 'section' || item.type === 'section') {
        response = await tocApi.toggleSectionVisibility(item.section_id, getClassContext());
      } else {
        response = await tocApi.toggleDocumentVisibility(item.document_id, getClassContext());
      }

      if (response.success) {
        const itemName = item.name || item.title;
        showSuccess(`"${itemName}" ${response.visible ? 'is now visible to' : 'is now hidden from'} students.`);
        loadTocData();
      } else {
        showError(response.error || 'Failed to toggle visibility');
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
      const itemName = item.name || item.title;
      showError(`Failed to toggle visibility for "${itemName}". Please try again.`);
    }
  };

  // ── Attachment handlers ───────────────────────────────────────────────────
  const handleAddFile = (item) => {
    const id = item.section_id || item.document_id;
    const type = item.type || (item.section_id ? 'section' : 'document');
    setAttachmentTarget({ id, type, name: item.name || item.title });
    loadAttachments(id);
  };

  const loadAttachments = async (parentId) => {
    try {
      const res = await attachmentsApi.listAttachments(parentId, getClassContext());
      if (res.success) {
        setAttachments(prev => ({ ...prev, [parentId]: res.attachments }));
      }
    } catch (e) { /* silent */ }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !attachmentTarget) return;
    setUploadingFile(true);
    try {
      const res = await attachmentsApi.uploadAttachment(
        attachmentTarget.id, attachmentTarget.type, file, getClassContext()
      );
      if (res.success) {
        showSuccess(`"${file.name}" attached successfully.`);
        loadAttachments(attachmentTarget.id);
      } else {
        showError(res.error || 'Upload failed');
      }
    } catch (e) {
      showError('Upload failed');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId, parentId) => {
    try {
      const res = await attachmentsApi.deleteAttachment(attachmentId, getClassContext());
      if (res.success) {
        setAttachments(prev => ({
          ...prev,
          [parentId]: (prev[parentId] || []).filter(a => a.attachment_id !== attachmentId)
        }));
      }
    } catch (e) { showError('Failed to delete attachment'); }
  };

  // Also load attachments for sections already visible when TOC loads
  const loadAllSectionAttachments = async (sections) => {
    for (const s of sections) {
      loadAttachments(s.section_id);
      if (s.children?.length) loadAllSectionAttachments(s.children);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleCreateSection = async (name) => {
    try {
      await tocApi.createSection(name.trim(), '', null, getClassContext());
      loadTocData();
    } catch (err) {
      console.error('Error creating section:', err);
      setError('Failed to create section');
    }
  };

  const handleAddSubsection = (parentSectionId) => {
    setNewSubsectionParentId(parentSectionId);
    setShowNewSubsectionModal(true);
  };

  const handleCreateSubsection = async (name) => {
    try {
      await tocApi.createSection(name.trim(), '', newSubsectionParentId, getClassContext());
      setCollapsedSections(prev => { const n = new Set(prev); n.delete(newSubsectionParentId); return n; });
      loadTocData();
    } catch (err) {
      console.error('Error creating subsection:', err);
      setError('Failed to create subsection');
    }
  };

  const handleAddDocumentToSection = async (sectionId) => {
    try {
      const title = `New Document`;
      await documentApi.createDocument(title, '', null, sectionId, '', getClassContext());
      loadTocData();
      setCollapsedSections(prev => { const n = new Set(prev); n.delete(sectionId); return n; });
    } catch (err) {
      console.error('Error adding document to section:', err);
      showError('Failed to create document. Please try again.');
    }
  };

  const handleMoveToSection = async (documentId, sectionId) => {
    const document = tocData.documents.find(doc => doc.document_id === documentId);
    if (!document) return;

    try {
      let newOrderIndex = 0;
      if (sectionId) {
        const sectionDocs = tocData.section_documents[sectionId] || [];
        newOrderIndex = sectionDocs.length;
      } else {
        newOrderIndex = tocData.root_documents.length;
      }

      await tocApi.reorderItems([{
        type: 'document',
        id: documentId,
        order_index: newOrderIndex,
        section_id: sectionId
      }], getClassContext());

      const updatedDocument = { ...document, section_id: sectionId };

      const oldSectionId = document.section_id;
      let newRootDocuments = [...tocData.root_documents];
      let newSectionDocuments = { ...tocData.section_documents };

      if (oldSectionId) {
        newSectionDocuments[oldSectionId] = (newSectionDocuments[oldSectionId] || [])
          .filter(doc => doc.document_id !== documentId);
      } else {
        newRootDocuments = newRootDocuments.filter(doc => doc.document_id !== documentId);
      }

      if (sectionId) {
        if (!newSectionDocuments[sectionId]) {
          newSectionDocuments[sectionId] = [];
        }
        newSectionDocuments[sectionId] = [...newSectionDocuments[sectionId], updatedDocument];
      } else {
        newRootDocuments = [...newRootDocuments, updatedDocument];
      }

      setTocData(prev => ({
        ...prev,
        root_documents: newRootDocuments,
        section_documents: newSectionDocuments,
        documents: prev.documents.map(doc =>
          doc.document_id === documentId ? updatedDocument : doc
        )
      }));

    } catch (err) {
      console.error('Error moving document:', err);
      setError('Failed to move document');
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const activeItem = findItemById(active.id);
    justDraggedRef.current = true; // block clicks fired by drag release
    setIsDragging(true);
    setDraggedItem(activeItem);
    nestTargetIdRef.current = null;
    nestHoverTargetRef.current = null;
    setNestTargetId(null);
    setDropIndicator(null);
    if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
  };

  const handleDragCancel = () => {
    setIsDragging(false);
    setDraggedItem(null);
    nestTargetIdRef.current = null;
    nestHoverTargetRef.current = null;
    setNestTargetId(null);
    setDropIndicator(null);
    if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) {
      if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
      setNestTargetId(null);
      setDropIndicator(null);
      return;
    }

    const activeItem = findItemById(active.id);

    let overId = over.id;
    if (typeof overId === 'string' && overId.startsWith('section-drop-')) {
      overId = overId.replace('section-drop-', '');
    }
    const overItem = findItemById(overId);

    const currentParentId = activeItem?.parent_section_id ?? null;

    if (
      activeItem?.type === 'section' &&
      overItem?.type === 'section' &&
      active.id !== overId &&
      overId !== currentParentId  // skip nesting into own current parent
    ) {
      // Only start a new timer when moving to a DIFFERENT section target
      if (nestHoverTargetRef.current !== overId && nestTargetIdRef.current !== overId) {
        if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
        nestHoverTargetRef.current = overId;
        nestHoverTimerRef.current = setTimeout(() => {
          nestTargetIdRef.current = overId;
          nestHoverTargetRef.current = null;
          setNestTargetId(overId);
          setDropIndicator(null);
          setCollapsedSections(prev => { const n = new Set(prev); n.delete(overId); return n; });
        }, 800);
      }
    } else {
      // Cancel pending timer but keep any already-committed target
      if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
      nestHoverTargetRef.current = null;
      // Only clear committed target if we're now over a section that isn't valid
      // (moving to empty space should NOT cancel a committed nest)
      if (overItem?.type === 'section') {
        nestTargetIdRef.current = null;
        setNestTargetId(null);
      }
    }

    if (!nestTargetId && overId !== 'root-drop-zone' && overItem) {
      const delta = event.delta?.y ?? 0;
      setDropIndicator({ overId, position: delta >= 0 ? 'after' : 'before' });
    } else if (overId === 'root-drop-zone') {
      setDropIndicator(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (nestHoverTimerRef.current) clearTimeout(nestHoverTimerRef.current);
    setTimeout(() => { justDraggedRef.current = false; }, 200);
    setIsDragging(false);
    setDraggedItem(null);

    const currentNestTarget = nestTargetIdRef.current;
    nestTargetIdRef.current = null;
    setNestTargetId(null);
    setDropIndicator(null);

    if (!over || active.id === over.id) return;

    try {
      const activeItem = findItemById(active.id);

      // ── Nest section: timer committed a target → trust it regardless of exact release position
      if (activeItem?.type === 'section' && currentNestTarget && currentNestTarget !== active.id) {
        setCollapsedSections(prev => {
          const next = new Set(prev);
          next.delete(currentNestTarget);
          const removeAncestors = (sections) => {
            for (const s of sections) {
              if (s.section_id === currentNestTarget) return true;
              if (s.children?.length && removeAncestors(s.children)) {
                next.delete(s.section_id);
                return true;
              }
            }
            return false;
          };
          removeAncestors(tocData.sections);
          return next;
        });
        await tocApi.reparentSection(active.id, currentNestTarget, getClassContext());
        await loadTocData();
        return;
      }

      // ── Move section to root
      if (activeItem?.type === 'section' && over.id === 'root-drop-zone') {
        await tocApi.reparentSection(active.id, null, getClassContext());
        await loadTocData();
        return;
      }

      // ── Document dropped on a section drop zone
      if (over.id.toString().startsWith('section-drop-') || over.id === 'root-drop-zone') {
        const sectionId = over.data?.current?.section_id;
        if (activeItem?.type === 'document') {
          await handleMoveToSection(activeItem.document_id, sectionId);
          return;
        }
        // Section dragged over a drop zone but no nest target — ignore, don't reorder
        return;
      }

      const overItem = findItemById(over.id);
      if (!activeItem || !overItem) return;

      if (activeItem.type === 'document' && overItem.type === 'section') {
        await handleMoveToSection(activeItem.document_id, overItem.section_id);
      } else if (activeItem.type === 'document' && overItem.type === 'document') {
        const activeSectionId = activeItem.section_id || null;
        const overSectionId = overItem.section_id || null;
        if (activeSectionId !== overSectionId) {
          await handleMoveToSection(activeItem.document_id, overSectionId);
        } else {
          await reorderDocuments(activeItem, overItem);
        }
      } else if (activeItem.type === 'section' && overItem.type === 'section') {
        // Only reorder if both sections share the same parent — never reorder across levels
        const activeSiblings = getSiblingArray(activeItem.section_id);
        const overInSameSiblings = activeSiblings?.find(s => s.section_id === overItem.section_id);
        if (overInSameSiblings) {
          await reorderSections(activeItem, overItem);
        }
      }
    } catch (err) {
      console.error('Error during drag and drop:', err);
      setError('Failed to move item');
    }
  };

  const findItemById = (id) => {
    const searchSections = (sections) => {
      for (const s of sections) {
        if (s.section_id === id) return { ...s, type: 'section' };
        if (s.children?.length) {
          const found = searchSections(s.children);
          if (found) return found;
        }
      }
      return null;
    };
    const section = searchSections(tocData.sections);
    if (section) return section;

    const doc = tocData.documents.find(d => d.document_id === id);
    if (doc) return { ...doc, type: 'document' };

    return null;
  };

  const reorderDocuments = async (activeDoc, overDoc) => {
    const activeSectionId = activeDoc.section_id || null;
    const containerDocs = activeSectionId
      ? tocData.section_documents[activeSectionId] || []
      : tocData.root_documents;

    const activeIndex = containerDocs.findIndex(d => d.document_id === activeDoc.document_id);
    const overIndex = containerDocs.findIndex(d => d.document_id === overDoc.document_id);

    if (activeIndex === -1 || overIndex === -1) return;

    const reorderedDocs = arrayMove(containerDocs, activeIndex, overIndex);
    const originalDocs = [...containerDocs];

    if (activeSectionId) {
      setTocData(prev => ({
        ...prev,
        section_documents: {
          ...prev.section_documents,
          [activeSectionId]: reorderedDocs
        }
      }));
    } else {
      setTocData(prev => ({
        ...prev,
        root_documents: reorderedDocs
      }));
    }

    try {
      const reorderItems = reorderedDocs.map((doc, index) => ({
        type: 'document',
        id: doc.document_id,
        order_index: index,
        section_id: activeSectionId
      }));

      await tocApi.reorderItems(reorderItems, getClassContext());
    } catch (error) {
      console.error('Failed to update server order:', error);
      if (activeSectionId) {
        setTocData(prev => ({
          ...prev,
          section_documents: {
            ...prev.section_documents,
            [activeSectionId]: originalDocs
          }
        }));
      } else {
        setTocData(prev => ({
          ...prev,
          root_documents: originalDocs
        }));
      }
      setError('Failed to reorder documents');
    }
  };

  const reorderSections = async (activeSection, overSection) => {
    const findSiblingArray = (sections, a, b) => {
      if (sections.find(s => s.section_id === a) && sections.find(s => s.section_id === b)) {
        return sections;
      }
      for (const s of sections) {
        if (s.children?.length) {
          const found = findSiblingArray(s.children, a, b);
          if (found) return found;
        }
      }
      return null;
    };

    const siblings = findSiblingArray(tocData.sections, activeSection.section_id, overSection.section_id);
    if (!siblings) return;

    const activeIndex = siblings.findIndex(s => s.section_id === activeSection.section_id);
    const overIndex = siblings.findIndex(s => s.section_id === overSection.section_id);
    if (activeIndex === -1 || overIndex === -1) return;

    const reorderedSiblings = arrayMove(siblings, activeIndex, overIndex);

    try {
      const reorderItems = reorderedSiblings.map((section, index) => ({
        type: 'section',
        id: section.section_id,
        order_index: index
      }));
      await tocApi.reorderItems(reorderItems, getClassContext());
      await loadTocData();
    } catch (error) {
      console.error('Failed to reorder sections:', error);
      setError('Failed to reorder sections');
    }
  };

  // Returns the sibling array containing the given sectionId (or null if not found)
  const getSiblingArray = (sectionId) => {
    const search = (sections) => {
      if (sections.find(s => s.section_id === sectionId)) return sections;
      for (const s of sections) {
        if (s.children?.length) {
          const found = search(s.children);
          if (found) return found;
        }
      }
      return null;
    };
    return search(tocData.sections);
  };

  // ── Unified ordered list per container ────────────────────────────────────
  const getContainerItems = (parentSectionId) => {
    const childSections = parentSectionId === null
      ? tocData.sections
      : (tocData.sections
          ? (() => {
              const findChildren = (sections) => {
                for (const s of sections) {
                  if (s.section_id === parentSectionId) return s.children || [];
                  if (s.children?.length) {
                    const found = findChildren(s.children);
                    if (found !== null) return found; // null = not found; [] = found but empty
                  }
                }
                return null; // not found in this subtree
              };
              return findChildren(tocData.sections) || [];
            })()
          : []);

    const docs = parentSectionId === null
      ? tocData.root_documents
      : tocData.section_documents[parentSectionId] || [];

    return [
      ...childSections.map(s => ({ ...s, _itemType: 'section', _id: s.section_id, _order: s.order_index ?? 0 })),
      ...docs.map(d => ({ ...d, _itemType: 'document', _id: d.document_id, _order: d.order_index ?? 0 })),
    ].sort((a, b) => a._order - b._order);
  };

  const handleItemMove = async (itemId, itemType, parentSectionId, direction) => {
    const items = getContainerItems(parentSectionId);
    const idx = items.findIndex(i => i._id === itemId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= items.length) return;

    const reordered = [...items];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];

    try {
      await tocApi.reorderItems(
        reordered.map((item, i) =>
          item._itemType === 'section'
            ? { type: 'section', id: item.section_id, order_index: i }
            : { type: 'document', id: item.document_id, order_index: i, section_id: parentSectionId }
        ),
        getClassContext()
      );
      await loadTocData();
    } catch (e) {
      setError('Failed to reorder');
    }
  };

  // Thin blue line shown above or below an item during drag
  const DropLine = ({ itemId, position }) => {
    const show = dropIndicator?.overId === itemId && dropIndicator?.position === position && !nestTargetId;
    if (!show) return null;
    return (
      <div className="h-0.5 bg-blue-500 rounded-full mx-3 my-0.5" />
    );
  };

  const renderDocument = (document, isIndented = false) => {
    if (isStudent) {
      return (
        <div
          key={document.document_id}
          className={`flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 cursor-pointer ${
            isIndented ? 'ml-6' : ''
          }`}
          onClick={() => handleDocumentClick(document)}
        >
          <MdDescription
            size={16}
            className="text-blue-600 mr-2 flex-shrink-0"
          />
          <span className="text-sm text-gray-700 flex-1 truncate">
            {document.title}
          </span>
        </div>
      );
    } else {
      const docSectionId = document.section_id || null;
      const docContainer = getContainerItems(docSectionId);
      const docIdx = docContainer.findIndex(i => i._id === document.document_id);

      return (
        <div key={document.document_id}>
          <DropLine itemId={document.document_id} position="before" />
          <SortableItem id={document.document_id} type="document" activeDragId={draggedItem?.document_id || draggedItem?.section_id}>
            <div
              className={`flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 cursor-pointer group ${
                isIndented ? 'ml-6' : ''
              }`}
              onClick={() => handleDocumentClick(document)}
              onContextMenu={(e) => handleContextMenu(e, { ...document, type: 'document' })}
            >
              <MdDescription
                size={16}
                className="text-blue-600 mr-2 flex-shrink-0"
              />
              <span className={`text-sm flex-1 truncate ${
                document.visible === false ? 'text-gray-400 italic' : 'text-gray-700'
              }`}>
                {document.title}
              </span>
              <button
                className={`p-0.5 rounded hover:bg-gray-200 flex-shrink-0 ${document.visible === false ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                onClick={(e) => { e.stopPropagation(); handleToggleVisibility({ ...document, type: 'document' }); }}
                title={document.visible === false ? 'Hidden from students — click to show' : 'Visible to students — click to hide'}
              >
                {document.visible === false
                  ? <MdVisibilityOff size={14} className="text-orange-400" />
                  : <MdVisibility size={14} className="text-gray-400" />}
              </button>
              {docContainer.length > 1 && (
                <div className="opacity-0 group-hover:opacity-100 flex items-center">
                  <button
                    className={`p-0.5 rounded hover:bg-gray-200 ${docIdx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleItemMove(document.document_id, 'document', docSectionId, 'up'); }}
                    disabled={docIdx === 0}
                    title="Move up"
                  >
                    <MdArrowUpward size={13} className="text-gray-500" />
                  </button>
                  <button
                    className={`p-0.5 rounded hover:bg-gray-200 ${docIdx === docContainer.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleItemMove(document.document_id, 'document', docSectionId, 'down'); }}
                    disabled={docIdx === docContainer.length - 1}
                    title="Move down"
                  >
                    <MdArrowDownward size={13} className="text-gray-500" />
                  </button>
                </div>
              )}
              <button
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  handleContextMenu(e, { ...document, type: 'document' });
                }}
              >
                <MdMoreVert size={14} className="text-gray-400" />
              </button>
            </div>
          </SortableItem>
          <DropLine itemId={document.document_id} position="after" />
        </div>
      );
    }
  };

  // Droppable Section Component
  const DroppableSection = ({ section }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: `section-drop-${section.section_id}`,
      data: { type: 'section', section_id: section.section_id }
    });

    const isExpanded = !collapsedSections.has(section.section_id);
    const sectionGuideline = tocData.section_guidelines?.[section.section_id] || null;

    const isNestTarget = nestTargetId === section.section_id;

    const parentId = section.parent_section_id ?? null;
    const containerItems = getContainerItems(parentId);
    const itemIdx = containerItems.findIndex(i => i._id === section.section_id);

    const combinedChildren = getContainerItems(section.section_id);

    return (
      <div key={section.section_id} className="mb-1">
        <DropLine itemId={section.section_id} position="before" />
        <SortableItem id={section.section_id} type="section" activeDragId={draggedItem?.document_id || draggedItem?.section_id}>
          <div
            ref={setNodeRef}
            className={`flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 group cursor-pointer ${
              isNestTarget
                ? 'bg-indigo-50 ring-2 ring-indigo-400 ring-inset'
                : isOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : ''
            }`}
            onContextMenu={(e) => handleContextMenu(e, { ...section, type: 'section', name: section.name })}
          >
            <button
              className="flex items-center justify-center w-4 h-4 mr-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(section.section_id);
              }}
            >
              {isExpanded ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
            </button>

            <MdFolder
              size={16}
              className="text-yellow-600 mr-2 flex-shrink-0"
            />

            <span className={`text-sm font-semibold flex-1 truncate ${
              section.visible === false ? 'text-gray-400 italic' : 'text-gray-800'
            }`}>
              {section.name}
            </span>

            <button
              className={`p-0.5 rounded hover:bg-gray-200 flex-shrink-0 ${section.visible === false ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
              onClick={(e) => { e.stopPropagation(); handleToggleVisibility({ ...section, type: 'section' }); }}
              title={section.visible === false ? 'Hidden from students — click to show' : 'Visible to students — click to hide'}
            >
              {section.visible === false
                ? <MdVisibilityOff size={14} className="text-orange-400" />
                : <MdVisibility size={14} className="text-gray-400" />}
            </button>
            {isNestTarget && (
              <span className="text-xs text-indigo-600 font-medium mr-1 flex-shrink-0">nest inside ↓</span>
            )}
            {containerItems.length > 1 && (
              <div className="opacity-0 group-hover:opacity-100 flex items-center">
                <button
                  className={`p-0.5 rounded hover:bg-gray-200 ${itemIdx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleItemMove(section.section_id, 'section', parentId, 'up'); }}
                  disabled={itemIdx === 0}
                  title="Move up"
                >
                  <MdArrowUpward size={13} className="text-gray-500" />
                </button>
                <button
                  className={`p-0.5 rounded hover:bg-gray-200 ${itemIdx === containerItems.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleItemMove(section.section_id, 'section', parentId, 'down'); }}
                  disabled={itemIdx === containerItems.length - 1}
                  title="Move down"
                >
                  <MdArrowDownward size={13} className="text-gray-500" />
                </button>
              </div>
            )}
            <button
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded"
              onClick={(e) => {
                e.stopPropagation();
                handleContextMenu(e, { ...section, type: 'section', name: section.name });
              }}
            >
              <MdMoreVert size={14} className="text-gray-400" />
            </button>
          </div>
        </SortableItem>

        {isExpanded && (
          <div className="ml-4">
            {onEditGuideline && (
              <button
                onClick={() => onEditGuideline({ ...section, section_id: section.section_id, name: section.name, sectionGuideline })}
                className="w-full flex items-center py-1.5 px-3 hover:bg-indigo-100 rounded-lg group transition-colors duration-150 mb-0.5"
                title={sectionGuideline ? 'Edit AI Guideline / Scaffold' : 'Create AI Guideline / Scaffold'}
              >
                <MdAutoAwesome size={14} className={`mr-2 flex-shrink-0 ${sectionGuideline ? 'text-indigo-600' : 'text-indigo-300 group-hover:text-indigo-500'}`} />
                <span className={`text-xs truncate ${sectionGuideline ? 'text-indigo-800 font-semibold' : 'text-indigo-400 italic group-hover:text-indigo-600'}`}>
                  {sectionGuideline ? (sectionGuideline.title || 'AI Guideline') : '+ AI Guideline / Scaffold'}
                </span>
              </button>
            )}
            {/* Attached reference files — instructor only, never shown to students */}
            {(attachments[section.section_id] || []).length > 0 && (
              <div className="mb-1">
                {(attachments[section.section_id] || []).map(a => (
                  <div key={a.attachment_id} className="flex items-center py-1 px-3 rounded-lg mx-2 text-xs text-gray-500 group/att hover:bg-gray-50">
                    <MdAttachFile size={13} className="text-gray-400 mr-1.5 flex-shrink-0" />
                    <button
                      onClick={() => attachmentsApi.openInNewTab(a.attachment_id, getClassContext())}
                      className="flex-1 truncate text-blue-600 hover:underline text-left"
                      title={a.filename}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                    >{a.filename}</button>
                    <span className="text-gray-300 mr-1">{a.extracted_chars ? `${Math.round(a.extracted_chars/1000)}k chars` : ''}</span>
                    <button
                      className="opacity-0 group-hover/att:opacity-100 p-0.5 hover:text-red-500 rounded"
                      onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(a.attachment_id, section.section_id); }}
                      title="Remove attachment"
                    >
                      <MdDelete size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {combinedChildren.map(item =>
              item._itemType === 'section'
                ? renderSection(item)
                : renderDocument(item, true)
            )}
          </div>
        )}
        <DropLine itemId={section.section_id} position="after" />
      </div>
    );
  };

  const renderSection = (section) => {
    if (isStudent) {
      const isExpanded = !collapsedSections.has(section.section_id);
      const containerItems = getContainerItems(section.section_id);

      return (
        <div key={section.section_id} className="mb-1">
          <div
            className="flex items-center py-1.5 px-3 hover:bg-gray-50/80 transition-colors duration-200 rounded-lg mx-2 cursor-pointer"
          >
            <button
              className="flex items-center justify-center w-4 h-4 mr-2 text-gray-400 hover:text-gray-600 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(section.section_id);
              }}
            >
              {isExpanded ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
            </button>
            <MdFolder
              size={16}
              className="text-yellow-600 mr-2 flex-shrink-0"
            />
            <span className="text-sm font-semibold text-gray-800 flex-1 truncate">
              {section.name}
            </span>
            <span className="text-xs text-gray-400 ml-2">({containerItems.length})</span>
          </div>

          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {containerItems.map(item =>
                item._itemType === 'section' ? renderSection(item) : renderDocument(item)
              )}
            </div>
          )}
        </div>
      );
    } else {
      return <DroppableSection key={section.section_id} section={section} />;
    }
  };

  if (loading) {
    return (
      <div className={`h-full bg-gray-50/80 border-r border-gray-200/60 flex flex-col ${className}`}>
        <div className="h-16 bg-white border-b border-gray-200/60 flex items-center px-4 flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-800">Table of Contents</h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  const flattenSections = (sections) => {
    const result = [];
    for (const s of sections) {
      result.push(s);
      if (s.children?.length) result.push(...flattenSections(s.children));
    }
    return result;
  };
  const allSectionsFlat = flattenSections(tocData.sections);
  const allItems = [...tocData.sections, ...tocData.root_documents];
  const allDraggableIds = [
    ...allSectionsFlat.map(s => s.section_id),
    ...tocData.documents.map(d => d.document_id),
  ];

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`h-full bg-gray-50/80 border-r border-gray-200/60 flex flex-col ${className}`} onClick={closeContextMenu}>
      <div className="h-16 bg-white border-b border-gray-200/60 flex items-center justify-between px-4 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800">Table of Contents</h3>
        {!isStudent && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowArchiveModal(true)}
              className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors duration-200"
              title="View archived items"
            >
              <MdArchive size={14} />
            </button>
            <button
              onClick={() => setShowNewSectionModal(true)}
              className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors duration-200"
              title="New section"
            >
              <MdCreateNewFolder size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {isStudent ? (
          <div className="min-h-full">
            {allItems.length === 0 ? (
              <div className="p-6 text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center shadow-sm">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Class Materials</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Course documents and resources will appear here when your instructor publishes them.
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 rounded-lg p-4">
                  <p className="text-xs text-blue-700 font-medium mb-1">What you'll find here:</p>
                  <div className="space-y-1 text-xs text-blue-600">
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Lecture notes and study materials</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Assignment instructions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                      <span>Course resources and references</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {getContainerItems(null).map(item =>
                  item._itemType === 'section'
                    ? renderSection(item)
                    : renderDocument(item)
                )}
              </>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragCancel={handleDragCancel}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div
              ref={setRootDropRef}
              className={`min-h-full ${isRootOver ? 'bg-blue-50/50' : ''}`}
            >
              {isDragging && draggedItem?.type === 'section' && draggedItem?.parent_section_id && (
                <div
                  className={`mx-3 mb-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs font-medium text-center transition-colors ${
                    isRootOver
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-300 text-gray-400'
                  }`}
                >
                  ↑ Drop here to move to root level
                </div>
              )}
              <SortableContext items={allDraggableIds} strategy={() => null}>
                {allItems.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="text-gray-500 text-sm mb-4">
                      <p className="mb-3 font-medium">No documents or sections found.</p>
                      <div className="bg-gray-50/80 border border-gray-200/60 rounded-lg p-4 text-left">
                        <p className="text-xs text-gray-600 mb-2">Create sections to organize your documents like folders in VSCode.</p>
                        <p className="text-xs text-gray-600">Drag and drop to reorder items.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowNewSectionModal(true)}
                      className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200"
                    >
                      <span>Create Section</span>
                      <MdAdd size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    {getContainerItems(null).map(item =>
                      item._itemType === 'section'
                        ? renderSection(item)
                        : renderDocument(item)
                    )}
                    {isRootOver && (
                      <div className="mx-2 mt-2 p-2 border-2 border-blue-300 border-dashed rounded-lg bg-blue-50/80">
                        <p className="text-xs text-blue-600 text-center">Drop here to move to root level</p>
                      </div>
                    )}
                  </>
                )}
              </SortableContext>
            </div>

            <DragOverlay dropAnimation={null}>
              {isDragging && draggedItem ? (
                <div className="bg-white border border-blue-400 shadow-lg rounded-lg px-3 py-1.5 flex items-center gap-2 text-sm font-medium text-gray-700 opacity-90 pointer-events-none">
                  {draggedItem.type === 'section'
                    ? <MdFolder size={16} className="text-yellow-600 flex-shrink-0" />
                    : <MdDescription size={16} className="text-blue-600 flex-shrink-0" />
                  }
                  <span className="truncate max-w-[160px]">
                    {draggedItem.name || draggedItem.title}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {!isStudent && (
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          item={contextMenu.item}
          onClose={closeContextMenu}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onArchive={handleArchive}
          onAddSection={handleAddDocumentToSection}
          onAddSubsection={handleAddSubsection}
          onMoveToSection={handleMoveToSection}
          onMoveToRoot={async (sectionId) => {
            await tocApi.reparentSection(sectionId, null, getClassContext());
            await loadTocData();
          }}
          onToggleVisibility={handleToggleVisibility}
          onEditGuideline={onEditGuideline || null}
          onAddFile={(item) => {
            handleAddFile(item);
            attachFileInputRef.current?.click();
          }}
          sections={tocData.sections}
        />
      )}

      {/* Hidden file input for attachment uploads */}
      {!isStudent && (
        <input
          ref={attachFileInputRef}
          type="file"
          accept=".pdf,.docx,.doc,.txt,.md"
          className="hidden"
          onChange={handleFileSelected}
        />
      )}

      {/* Attachment manager modal */}
      {!isStudent && attachmentTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[480px] max-w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MdAttachFile size={20} />
                Reference Files — {attachmentTarget.name}
              </h3>
              <button onClick={() => setAttachmentTarget(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Attached files are used as AI reference material (RAG) for document generation and student Q&A. They are never visible to students.
            </p>
            {/* Existing attachments */}
            {(attachments[attachmentTarget.id] || []).length === 0 ? (
              <p className="text-sm text-gray-400 italic mb-4">No files attached yet.</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {(attachments[attachmentTarget.id] || []).map(a => (
                  <div key={a.attachment_id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <MdAttachFile size={16} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => attachmentsApi.openInNewTab(a.attachment_id, getClassContext())}
                        className="text-sm text-blue-600 hover:underline truncate block text-left w-full"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                      >
                        {a.filename}
                      </button>
                      <p className="text-xs text-gray-400">{a.extracted_chars ? `${Math.round(a.extracted_chars/1000)}k chars extracted` : 'No text extracted'}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteAttachment(a.attachment_id, attachmentTarget.id)}
                      className="p-1 hover:bg-red-50 hover:text-red-500 rounded text-gray-400"
                      title="Remove"
                    >
                      <MdDelete size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Upload button */}
            <button
              onClick={() => attachFileInputRef.current?.click()}
              disabled={uploadingFile}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploadingFile ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <MdUploadFile size={18} />
              )}
              <span>{uploadingFile ? 'Uploading...' : 'Upload File (PDF, DOCX, TXT)'}</span>
            </button>
          </div>
        </div>
      )}

      {!isStudent && (
        <EditModal
          isOpen={editModal.isOpen}
          item={editModal.item}
          title={editModal.title}
          onClose={() => setEditModal({ isOpen: false, item: null, title: '' })}
          onSave={handleSave}
        />
      )}

      {!isStudent && showNewSectionModal && (
        <EditModal
          isOpen={showNewSectionModal}
          item={{ name: '' }}
          title="Create New Section"
          onClose={() => setShowNewSectionModal(false)}
          onSave={(_, name) => {
            handleCreateSection(name);
            setShowNewSectionModal(false);
          }}
        />
      )}

      {!isStudent && showNewSubsectionModal && (
        <EditModal
          isOpen={showNewSubsectionModal}
          item={{ name: '' }}
          title="Create New Subsection"
          onClose={() => { setShowNewSubsectionModal(false); setNewSubsectionParentId(null); }}
          onSave={(_, name) => {
            handleCreateSubsection(name);
            setShowNewSubsectionModal(false);
            setNewSubsectionParentId(null);
          }}
        />
      )}

      {!isStudent && (
        <ArchiveModal
          isOpen={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          archivedItems={archivedItems}
          onUnarchive={handleUnarchive}
          loading={loadingArchive}
        />
      )}
    </div>
  );
};

export default TableOfContents;
